/**
 * Popover listing `@workshop` / `@phase` / `@output` / `@asset`
 * candidates as the user types. Returns the chosen reference to its
 * parent (`ChatInput` or `ContextBar`).
 *
 * Visual chrome matches the legacy `#chatContextDropdown` in
 * `_legacy/wd.html` (lines 665-674 + 2022-2070):
 *   - min-width 260 px popover (no max), true-blue translucent border,
 *     soft cyan glow. Outer wrapper has no padding — the items area
 *     owns its own `6px 0` vertical rhythm, matching the legacy
 *     `#chatContextDropdownItems` container.
 *   - Single flat row stream — no section headers, no dividers
 *     between groups, no search input. Candidate order is per-workshop
 *     interleaved (workshop overview → phases → outputs → uploads →
 *     ... → next workshop), exactly as `listReferenceCandidates`
 *     emits them.
 *   - Each row is a three-column grid: 40 px id badge | flex label |
 *     trailing glyph (lucide `Check` sky for selected).
 *   - Filtering is driven entirely by the upstream `@`-tail `query`
 *     prop passed in from `ChatInput`. The bar-picker opens with
 *     `query=""` and surfaces all candidates in a scrollable list,
 *     matching the legacy "scroll through everything" affordance.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Check } from "lucide-react";
import {
  listReferenceCandidates,
  type ReferenceKind,
  type XaesReference,
} from "../reference-resolver";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { ChatScope } from "../context-builder";
import { refKey } from "./ContextChips";

type Props = {
  /** Scope drives which workshops are surfaced — MC shows everything. */
  scope: ChatScope;
  /** Live filter text — the part of the input after `@`. */
  query: string;
  /** Whether the popover is open. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fires when the user picks a candidate. */
  onSelect: (ref: XaesReference) => void;
  /** Currently-active reference keys (chip set) — drives the tick state. */
  activeKeys?: ReadonlySet<string>;
  /** Anchor — `ChatInput` mounts this around the `@` button or `+ Add` pill. */
  children: React.ReactNode;
  /** Side the popover prefers. Defaults to `top` (matches legacy bottom-anchored bar). */
  side?: "top" | "bottom" | "left" | "right";
  /**
   * Whitelist of candidate kinds the picker should surface. When
   * omitted, every kind is shown (the rich `@`-typed picker). The
   * bar +Add picker passes `["workshop"]` to restore the legacy
   * workshops-only flat list at `_legacy/wd.html` lines 1996-2005.
   */
  kindFilter?: ReadonlyArray<ReferenceKind>;
};

export function ReferencePicker({
  scope,
  query,
  open,
  onOpenChange,
  onSelect,
  activeKeys,
  children,
  side = "top",
  kindFilter,
}: Props) {
  // Recompute candidates whenever the picker opens so newly-added
  // uploads from `useAssetLocker` flow in without a remount.
  const candidates = useMemo(() => {
    if (!open) return [];
    // When the bar surfaces a workshops-only list, source from every
    // workshop regardless of the current dashboard scope. The legacy
    // `+Add` dropdown (`_legacy/wd.html` lines 1996-2005) listed all 8
    // workshops at all times so facilitators could attach
    // cross-workshop context. Restricting to `scope === "WS4"` (the
    // default) would yield a single row, which is not useful.
    const workshopsOnly =
      kindFilter?.length === 1 && kindFilter[0] === "workshop";
    const sourceScope = workshopsOnly ? "MC" : scope;
    const all = listReferenceCandidates(sourceScope);
    if (!kindFilter || kindFilter.length === 0) return all;
    const allow = new Set(kindFilter);
    return all.filter((c) => allow.has(c.kind));
  }, [scope, open, kindFilter]);

  // Filter driven entirely by the upstream `@`-tail query (passed in
  // from `ChatInput`). Case-insensitive `includes` match against the
  // label, kind, workshopId, and assetName.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        c.kind.toLowerCase().includes(q) ||
        c.workshopId.toLowerCase().includes(q) ||
        (c.assetName?.toLowerCase().includes(q) ?? false),
    );
  }, [candidates, query]);

  // Flat list — matches the legacy `chatContextDropdownItems` single
  // stream. `listReferenceCandidates` already emits items in
  // per-workshop interleaved order (WS1 overview → WS1 phases → WS1
  // outputs → WS1 uploads → WS2 overview → ...), and `filtered`
  // preserves that order, so no resorting is needed.
  const flat = filtered;

  // Highlighted index — starts at -1 so the popover opens with no row
  // washed, matching legacy where the wash only appears on actual mouse
  // hover. Keyboard ArrowDown bumps it into the list (0).
  const [highlight, setHighlight] = useState(-1);
  useEffect(() => {
    setHighlight(-1);
  }, [query, scope, open]);

  // Keyboard nav while the popover is open. We attach listeners to
  // the document so the input's keydown handler keeps focus.
  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (flat.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => (h < 0 ? 0 : (h + 1) % flat.length));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) =>
          h < 0 ? flat.length - 1 : (h - 1 + flat.length) % flat.length,
        );
      } else if (e.key === "Enter") {
        if (highlight < 0) return;
        const picked = flat[highlight];
        if (picked) {
          e.preventDefault();
          onSelect(picked);
        }
      } else if (e.key === "Escape") {
        onOpenChange(false);
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, flat, highlight, onSelect, onOpenChange]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="start"
        side={side}
        sideOffset={6}
        // Match legacy `#chatContextDropdown` chrome exactly: bg-panel +
        // true-blue 0.35 border + soft cyan glow. The outer wrapper has
        // **no padding** — the inner items container owns its own
        // rhythm, mirroring the legacy structure where
        // `#chatContextDropdown` carried no padding and the inner
        // `#chatContextDropdownItems` div supplied `padding:6px 0`.
        // `w-auto` overrides the shadcn default `w-72` so the dropdown
        // sizes to its content with only a `minWidth: 260` floor.
        className="w-auto p-0"
        style={{
          minWidth: 260,
          background: "var(--bg-panel)",
          border: "1px solid rgba(var(--accent-true-blue-rgb), 0.35)",
          borderRadius: 10,
          overflow: "hidden",
          boxShadow:
            "0 0 30px rgba(var(--accent-true-blue-rgb), 0.12), 0 8px 24px rgba(0, 0, 0, 0.3)",
        }}
      >
        <div
          ref={listRef}
          className="max-h-72 overflow-y-auto"
          // Mirrors legacy `#chatContextDropdownItems { padding:6px 0 }` —
          // vertical-only padding so each row's `8px 14px` is the only
          // horizontal indent. Scrollbar tinted to the legacy thin
          // true-blue thumb used throughout the chat column
          // (`_legacy/wd.html:633`).
          style={{
            padding: "6px 0",
            scrollbarWidth: "thin",
            scrollbarColor:
              "rgba(var(--accent-true-blue-rgb), 0.4) rgba(255, 255, 255, 0.03)",
          }}
        >
          {flat.length === 0 ? (
            <div
              style={{
                padding: "14px 14px",
                fontSize: "var(--fs-label)",
                fontWeight: 700,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                color: "var(--text-muted)",
              }}
            >
              No matches
            </div>
          ) : (
            flat.map((c, idx) => {
              const key = refKey(c);
              const isActive = activeKeys?.has(key) ?? false;
              const isHi = idx === highlight;
              // Hover wash mirrors the legacy 0.06 alpha.
              const rowBg = isHi
                ? "rgba(var(--accent-true-blue-rgb), 0.06)"
                : "transparent";
              return (
                <PickerRow
                  key={key}
                  candidate={c}
                  isActive={isActive}
                  background={rowBg}
                  onHover={() => setHighlight(idx)}
                  onClick={() => onSelect(c)}
                />
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Single dropdown row: `[40 px badge] [flex label] [trailing glyph]`.
 *
 * Rendered as a `<button>` for keyboard accessibility.
 */
function PickerRow({
  candidate,
  isActive,
  background,
  onHover,
  onClick,
}: {
  candidate: XaesReference;
  isActive: boolean;
  background: string;
  onHover: () => void;
  onClick: () => void;
}) {
  const badgeColor = isActive
    ? "var(--accent-sky)"
    : "var(--accent-true-blue)";
  // Label colour matches legacy `renderDropdown()` (wd.html:2040) — it
  // does NOT change for active rows; only the badge column does. Active
  // state is communicated via the trailing Check glyph.
  const labelColor = "var(--text-secondary)";
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onHover}
      className={cn("w-full text-left transition-colors")}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 14px",
        background,
        border: 0,
        cursor: "pointer",
        fontFamily: "Inter, sans-serif",
        color: labelColor,
        transition: "background 0.12s",
      }}
    >
      {/* Badge column — 64 px wide so "PREPARE" (the WS8 alias) fits
          without wrapping. Workshop rows use this slot for the parent
          id; the label column carries the kind disambiguation. */}
      <span
        style={{
          width: 64,
          flexShrink: 0,
          fontFamily: "Inter, sans-serif",
          fontSize: "var(--fs-base)",
          fontWeight: 700,
          letterSpacing: "1px",
          color: badgeColor,
          whiteSpace: "nowrap",
        }}
      >
        {badgeFor(candidate)}
      </span>
      <span
        style={{
          flex: 1,
          fontSize: "var(--fs-base)",
          lineHeight: 1.4,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          color: labelColor,
        }}
      >
        {labelForPicker(candidate)}
      </span>
      {/* Trailing slot — Check (sky) when active. Empty otherwise, but we
          keep the column reserved so rows align with active neighbours. */}
      <span
        aria-hidden
        style={{
          width: 12,
          height: 12,
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isActive ? (
          <Check
            style={{
              width: 12,
              height: 12,
              color: "var(--accent-sky)",
            }}
            strokeWidth={2.5}
          />
        ) : null}
      </span>
    </button>
  );
}

/** Short tag rendered in the 40 px badge column. Every row — workshop
 *  overview, phase, output, asset, briefing, finding — shows its
 *  parent `WS{n}` id, exactly mirroring the legacy
 *  `chatContextDropdown` where the badge column carried only the
 *  workshop number. The label column carries the kind disambiguation
 *  (e.g. "Phase 1: Discovery", "Transcript output"), and because
 *  `listReferenceCandidates` emits rows in per-workshop order the
 *  repeated `WS{n}` reads as a visual grouping rather than noise.
 *
 *  WS8 is rendered as "PREPARE" to match the legacy navigator
 *  (`_legacy/mc.html` line 2074), which treated the final cell as the
 *  flight-plan prep step rather than a numbered workshop.
 */
function badgeFor(ref: XaesReference): string {
  return ref.workshopId === "WS8" ? "PREPARE" : ref.workshopId;
}

/** Trim the workshop-id prefix from picker labels — the badge column
 *  already shows the workshop id, so showing it again is noisy. */
function labelForPicker(ref: XaesReference): string {
  const prefix = `${ref.workshopId} — `;
  if (ref.label.startsWith(prefix)) return ref.label.slice(prefix.length);
  return ref.label;
}
